import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { signOut } from '../modules/auth.js';
import { getOktaConfig, hasOktaConfig } from '../lib/oktaConfig.js';
import OktaSessionMonitor from '../lib/oktaSessionMonitor.js';

/**
 * Custom hook to manage Okta session monitoring
 * Initializes once on app mount and handles cleanup
 * Only runs for SSO users (checks for saved Okta config)
 */
export default function useOktaSessionMonitor() {
  const dispatch = useDispatch();
  const monitorRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Skip if already initialized or no Okta config exists
    if (initializedRef.current || !hasOktaConfig()) {
      return;
    }

    // Skip if window or OktaAuth not available
    if (typeof window === 'undefined' || !window.OktaAuth) {
      return;
    }

    const oktaConfig = getOktaConfig();
    if (!oktaConfig) {
      return;
    }

    try {
      // Initialize Okta Auth instance
      const oktaAuth = new window.OktaAuth({
        issuer: oktaConfig.issuer,
        clientId: oktaConfig.clientId,
        redirectUri: oktaConfig.redirectUri,
        scopes: oktaConfig.scopes,
        pkce: true,
        responseType: 'code',
        tokenManager: {
          autoRenew: false,
        },
      });

      // Create and start session monitor
      monitorRef.current = new OktaSessionMonitor(oktaAuth, () => {
        dispatch(signOut());
      });

      monitorRef.current.start();
      initializedRef.current = true;
    } catch (error) {
      console.warn('Failed to initialize Okta session monitoring:', error);
    }

    // Cleanup function
    return () => {
      if (monitorRef.current) {
        monitorRef.current.stop();
        monitorRef.current = null;
        initializedRef.current = false;
      }
    };
  }, [dispatch]);

  // Handle page unload cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (monitorRef.current) {
        monitorRef.current.stop();
        monitorRef.current = null;
        initializedRef.current = false;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
}
